import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import './ShiftsPage.css';
import AddExpenseButton from '../components/AddExpenseButton';
import ShiftSide from '../components/ShiftSide';

const pad = (n) => String(n).padStart(2, '0');
const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const rupees = (n, digits = 0) => '₹' + (Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: digits, maximumFractionDigits: digits });
const qty = (n) => (n == null ? '—' : Number(n).toLocaleString('en-IN', { maximumFractionDigits: 3 }));
const signed = (n, fmt = qty) => (n == null ? '—' : n > 0 ? `+${fmt(n)}` : fmt(n));
const niceDate = (s) => (s ? new Date(`${s}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const localTime = (utc) => (utc ? new Date(`${utc.replace(' ', 'T')}Z`).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : '');
const NOTES = [500, 200, 100, 50, 20, 10];

export default function ShiftsPage() {
  const [view, setView] = useState('current');
  const [data, setData] = useState(null);
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(() => api.get('/shifts/current').then(setData).catch((e) => setError(e.message)), []);
  useEffect(() => { load(); }, [load]);

  const openReport = async (id) => {
    try { setReport(await api.get(`/shifts/${id}/report`)); setView('report'); }
    catch (e) { setError(e.message); }
  };

  return (
    <div className="sh-root">
      <header className="sh-head">
        <div>
          <h1>Shift handover</h1>
          <p>Opening stock comes from the last shift's count. Sales come from billing. At the end of the shift, count everything without looking at the system numbers.</p>
        </div>
        <div className="sh-tabs">
          <button type="button" className={view !== 'history' ? 'is-active' : ''} onClick={() => { setView('current'); load(); }}>Current shift</button>
          <button type="button" className={view === 'history' ? 'is-active' : ''} onClick={() => setView('history')}>Past shifts</button>
        </div>
      </header>

      {error && <div className="sh-error" onClick={() => setError('')}>{error}</div>}

      {view === 'current' && data && (
        data.shift
          ? <OpenShift data={data} onError={setError} onClosed={(r) => { setReport(r); setView('report'); load(); }} />
          : <StartShift suggest={data.suggest} onError={setError} onStarted={load} onViewLast={openReport} />
      )}
      {view === 'report' && report && <ShiftReport report={report} onBack={() => { setView('current'); load(); }} />}
      {view === 'history' && <History onOpen={openReport} onError={setError} />}
    </div>
  );
}

/* ================= Start ================= */

function StartShift({ suggest, onError, onStarted, onViewLast }) {
  const [name, setName] = useState(suggest.name);
  const [cash, setCash] = useState(String(suggest.opening_cash ?? 0));
  const [date, setDate] = useState(todayStr());
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true);
    try { await api.post('/shifts/start', { name, opening_cash: cash, business_date: date }); onStarted(); }
    catch (e) { onError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="sh-card sh-start">
      <h2>Start a shift</h2>
      {suggest.previous ? (
        <p className="sh-muted">
          Last shift: {suggest.previous.name}, {niceDate(suggest.previous.business_date)}, closed by {suggest.previous.closed_by || '—'}.{' '}
          <button type="button" className="sh-link" onClick={() => onViewLast(suggest.previous.id)}>View its report</button>
        </p>
      ) : (
        <p className="sh-muted">This is the first shift. Opening stock is taken from the current system stock.</p>
      )}

      <div className="sh-field">
        <span>Shift</span>
        <div className="sh-seg">
          {['Morning', 'Night'].map((n) => (
            <button key={n} type="button" className={name === n ? 'is-active' : ''} onClick={() => setName(n)}>{n}</button>
          ))}
        </div>
      </div>
      <label className="sh-field">
        <span>Date</span>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </label>
      <label className="sh-field">
        <span>Opening cash in drawer (₹)</span>
        <input type="number" min="0" inputMode="decimal" value={cash} onChange={(e) => setCash(e.target.value)} />
        <small>Filled from the cash the last shift counted. Change it only if different.</small>
      </label>
      <button type="button" className="sh-primary sh-big" onClick={start} disabled={busy}>Start {name} shift</button>
    </div>
  );
}

/* ================= Open shift ================= */

function OpenShift({ data, onError, onClosed }) {
  const { shift, live, materials, is_owner: isOwner } = data;
  const [counting, setCounting] = useState(false);

  if (counting) return <CountSheet shift={shift} materials={materials} onError={onError} onCancel={() => setCounting(false)} onClosed={onClosed} />;

  return (
    <>
      <div className="sh-card sh-open">
        <div>
          <span className="sh-badge">Open</span>
          <h2>{shift.name} shift, {niceDate(shift.business_date)}</h2>
          <p className="sh-muted">Started {localTime(shift.opened_at)} by {shift.opened_by || '—'}</p>
        </div>
        <button type="button" className="sh-primary sh-big" onClick={() => setCounting(true)}>End shift and count stock</button>
      </div>

      <AddExpenseButton />
      <div className="sh-stats">
        <div><span>Bills</span><strong>{live.bill_count}</strong></div>
        <div><span>Sales</span><strong>{rupees(live.sales_total)}</strong></div>
        {(
          <>
            <div><span>Opening cash</span><strong>{rupees(shift.opening_cash)}</strong></div>
            <div><span>Cash sales</span><strong>{rupees(live.cash_sales)}</strong></div>
            <div><span>UPI</span><strong>{rupees(live.upi_sales)}</strong></div>
            <div><span>Card</span><strong>{rupees(live.card_sales)}</strong></div>
          </>
        )}
      </div>

      {(
        <><ShiftSide free={live.free_items || []} products={live.product_sales || []} /><CashSummary
          opening={shift.opening_cash}
          cats={live.category_sales || []}
        discountDetails={live.discount_details || []}
        products={live.product_sales || []}
          discounts={live.bill_discounts || 0}
          sales={live.sales_total}
          upi={live.upi_sales}
          card={live.card_sales}
          refunds={live.refunds}
          expenses={live.cash_expenses}
          expenseList={live.expense_details || []}
        /></>
      )}

    </>
  );
}

/* ================= Blind count ================= */

function CountSheet({ shift, materials, onError, onCancel, onClosed }) {
  const key = `icecream_shift_count_${shift.id}`;
  const [counts, setCounts] = useState(() => { try { return JSON.parse(localStorage.getItem(key) || '{}'); } catch (e) { return {}; } });
  const [cash, setCash] = useState(() => { try { return localStorage.getItem(`${key}_cash`) || ''; } catch (e) { return ''; } });
  const [upi, setUpi] = useState(() => { try { return localStorage.getItem(`${key}_upi`) || ''; } catch (e) { return ''; } });
  const [notes, setNotes] = useState({});
  const [step, setStep] = useState(() => (cash !== '' && upi !== '' ? 'stock' : 'money'));
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [onlyLeft, setOnlyLeft] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showNotes, setShowNotes] = useState(false);

  useEffect(() => { try { localStorage.setItem(key, JSON.stringify(counts)); } catch (e) {} }, [counts, key]);
  useEffect(() => { try { localStorage.setItem(`${key}_cash`, cash); } catch (e) {} }, [cash, key]);
  useEffect(() => { try { localStorage.setItem(`${key}_upi`, upi); } catch (e) {} }, [upi, key]);

  const categories = useMemo(() => ['All', ...new Set(materials.map((m) => m.category || 'Uncategorised'))], [materials]);
  const counted = materials.filter((m) => counts[m.id] !== undefined && counts[m.id] !== '').length;
  const term = search.trim().toLowerCase();
  const rows = materials.filter((m) =>
    (!term || m.name.toLowerCase().includes(term)) &&
    (category === 'All' || (m.category || 'Uncategorised') === category) &&
    (!onlyLeft || counts[m.id] === undefined || counts[m.id] === ''));

  const noteTotal = NOTES.reduce((t, n) => t + n * (Number(notes[n]) || 0), 0) + (Number(notes.coins) || 0);

  const submit = async () => {
    if (cash === '') return onError('Enter the cash counted in the drawer');
    if (upi === '') return onError('Enter the UPI total from the GPay/PhonePe app (0 if none)');
    const left = materials.length - counted;
    const msg = left > 0
      ? `${left} item(s) are not counted. They will carry forward the system quantity.\n\nClose the ${shift.name} shift now?`
      : `Close the ${shift.name} shift now? Counts cannot be changed after this.`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const report = await api.post('/shifts/close', { counts, counted_cash: cash, counted_upi: upi });
      try { localStorage.removeItem(key); localStorage.removeItem(`${key}_cash`); localStorage.removeItem(`${key}_upi`); } catch (e) {}
      onClosed(report);
    } catch (e) { onError(e.message); }
    finally { setBusy(false); }
  };

  if (step === 'money') {
    const ready = cash !== '' && upi !== '' && Number(cash) >= 0 && Number(upi) >= 0;
    return (
      <>
        <div className="sh-card sh-count-head">
          <div>
            <h2>Close {shift.name} shift · Step 1 of 2: Cash and UPI</h2>
            <p className="sh-muted">First count the cash in the drawer and check the UPI total in the GPay/PhonePe app. Stock counting comes next.</p>
          </div>
        </div>

      <div className="sh-card sh-cash">
        <h2>UPI (GPay / PhonePe)</h2>
        <label className="sh-field">
          <span>Total UPI received this shift, as shown in the app (₹)</span>
          <input type="number" min="0" inputMode="decimal" value={upi} onChange={(e) => setUpi(e.target.value)} onWheel={(e) => e.target.blur()} placeholder="Enter amount" />
        </label>
        <p className="sh-muted" style={{ margin: '6px 0 0' }}>Open the GPay/PhonePe business app, check today's payments since the shift started, and enter the total. Enter 0 if there were none.</p>
      </div>

      <div className="sh-card sh-cash">
        <h2>Cash in drawer</h2>
        <label className="sh-field">
          <span>Total cash counted (₹)</span>
          <input type="number" min="0" inputMode="decimal" value={cash} onChange={(e) => setCash(e.target.value)} placeholder="Enter amount" />
        </label>
        <button type="button" className="sh-link" onClick={() => setShowNotes((v) => !v)}>
          {showNotes ? 'Hide note counter' : 'Count by notes'}
        </button>
        {showNotes && (
          <div className="sh-notes">
            {NOTES.map((n) => (
              <label key={n}><span>₹{n} ×</span><input type="number" min="0" inputMode="numeric" value={notes[n] || ''} onChange={(e) => setNotes({ ...notes, [n]: e.target.value })} /></label>
            ))}
            <label><span>Coins ₹</span><input type="number" min="0" inputMode="decimal" value={notes.coins || ''} onChange={(e) => setNotes({ ...notes, coins: e.target.value })} /></label>
            <div className="sh-notes-total">
              Total {rupees(noteTotal)}
              <button type="button" className="sh-ghost" onClick={() => setCash(String(noteTotal))}>Use this total</button>
            </div>
          </div>
        )}
      </div>

        <div className="sh-actions">
          <button type="button" className="sh-ghost" onClick={onCancel}>Back</button>
          <button type="button" className="sh-primary sh-big" disabled={!ready} onClick={() => setStep('stock')}>
            Next: count stock
          </button>
        </div>
        {!ready && <p className="sh-muted" style={{ textAlign: 'right', marginTop: 6 }}>Enter both amounts (0 if none) to continue.</p>}
      </>
    );
  }

  return (
    <>
      <div className="sh-card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '12px 16px' }}>
        <span style={{ fontWeight: 700 }}>Cash {rupees(Number(cash))} · UPI {rupees(Number(upi))}</span>
        <button type="button" className="sh-link" onClick={() => setStep('money')}>Change</button>
      </div>

      <div className="sh-card sh-count-head">
        <div>
          <h2>Step 2 of 2: Count stock ({shift.name} shift)</h2>
          <p className="sh-muted">Count what is physically on the shelf and in the freezer. Leave an item empty only if you cannot count it.</p>
        </div>
        <div className="sh-progress">
          <strong>{counted} / {materials.length}</strong>
          <span>items counted</span>
          <div className="sh-bar"><i style={{ width: `${materials.length ? (counted / materials.length) * 100 : 0}%` }} /></div>
        </div>
      </div>

      <div className="sh-toolbar">
        <input type="text" placeholder="Search items" value={search} onChange={(e) => setSearch(e.target.value)} />
        <label className="sh-check"><input type="checkbox" checked={onlyLeft} onChange={(e) => setOnlyLeft(e.target.checked)} /> Only uncounted</label>
      </div>
      <div className="sh-chips">
        {categories.map((c) => (
          <button key={c} type="button" className={category === c ? 'is-active' : ''} onClick={() => setCategory(c)}>{c}</button>
        ))}
      </div>

      <div className="sh-count-list">
        {rows.map((m) => {
          const done = counts[m.id] !== undefined && counts[m.id] !== '';
          return (
            <label key={m.id} className={`sh-count-row ${done ? 'is-done' : ''}`}>
              <span className="sh-count-name">
                <strong>{m.name}</strong>
                <small>{m.category || 'Uncategorised'}{m.pack_size > 1 ? `, 1 box = ${m.pack_size} ${m.unit}` : ''}</small>
              </span>
              <span className="sh-count-input">
                <input
                  type="number"
                  min="0"
                  inputMode="decimal"
                  value={counts[m.id] ?? ''}
                  placeholder="Count"
                  onChange={(e) => setCounts({ ...counts, [m.id]: e.target.value })}
                  onWheel={(e) => e.target.blur()}
                />
                <em>{m.unit}</em>
              </span>
            </label>
          );
        })}
        {rows.length === 0 && <div className="sh-empty">Nothing to show here.</div>}
      </div>

      <div className="sh-actions">
        <button type="button" className="sh-ghost" onClick={onCancel} disabled={busy}>Back (counts are saved)</button>
        <button type="button" className="sh-primary sh-big" onClick={submit} disabled={busy}>
          {busy ? 'Closing…' : `Close ${shift.name} shift`}
        </button>
      </div>
    </>
  );
}

/* ================= Report ================= */

function ShiftReport({ report, onBack }) {
  const { shift, stock, product_sales: sales, summary } = report;
  const [showAll, setShowAll] = useState(true);
  const mismatched = stock.filter((r) => r.diff != null && Math.abs(r.diff) > 1e-9)
    .sort((a, b) => a.diff_value - b.diff_value);
  const uncounted = stock.filter((r) => r.counted == null);
  const moved = stock.filter((r) => r.sold || r.received || r.adjusted);
  const rows = showAll ? stock : moved;
  const cashDiff = summary.cash_diff;

  return (
    <>
      <div className="sh-card sh-report-head">
        <div>
          <h2>{shift.name} shift, {niceDate(shift.business_date)}</h2>
          <p className="sh-muted">
            {localTime(shift.opened_at)} ({shift.opened_by || '—'}) to {localTime(shift.closed_at)} ({shift.closed_by || '—'})
          </p>
        </div>
        <div className="sh-report-actions">
          <button type="button" className="sh-ghost" onClick={() => window.print()}>Print</button>
          <button type="button" className="sh-ghost" onClick={onBack}>Done</button>
        </div>
      </div>

      <div className="sh-print-title">Ice Bay x Hendy's Kitchen: {shift.name} shift, {niceDate(shift.business_date)}</div>

      <div className="sh-stats">
        <div><span>Bills</span><strong>{shift.bill_count}</strong></div>
        <div><span>Sales</span><strong>{rupees(shift.sales_total)}</strong></div>
        <div><span>Cash / UPI / Card</span><strong className="sh-small">{rupees(shift.cash_sales)} / {rupees(shift.upi_sales)} / {rupees(shift.card_sales)}</strong></div>
        <div className={cashDiff < 0 ? 'is-bad' : cashDiff > 0 ? 'is-warn' : 'is-good'}>
          <span>Cash: expected {rupees(shift.expected_cash)}, counted {rupees(shift.counted_cash)}</span>
          <strong>{cashDiff === 0 ? 'Matches' : cashDiff < 0 ? `${rupees(-cashDiff)} short` : `${rupees(cashDiff)} extra`}</strong>
        </div>
        {shift.counted_upi != null && (
          <div className={summary.upi_diff < 0 ? 'is-bad' : summary.upi_diff > 0 ? 'is-warn' : 'is-good'}>
            <span>UPI: bills {rupees(shift.upi_sales)}, app {rupees(shift.counted_upi)}</span>
            <strong>{Math.abs(summary.upi_diff) < 0.005 ? 'Matches' : summary.upi_diff < 0 ? `${rupees(-summary.upi_diff)} less in app` : `${rupees(summary.upi_diff)} more in app`}</strong>
          </div>
        )}
        <div className={summary.shortage_value > 0 ? 'is-bad' : 'is-good'}>
          <span>Stock shortage (at selling price)</span>
          <strong>{summary.mismatches === 0 ? 'None' : rupees(summary.shortage_value)}</strong>
        </div>
      </div>

      <><ShiftSide free={report.free_items || []} products={report.product_sales || []} /><CashSummary
        opening={shift.opening_cash}
        cats={report.category_sales || []}
        discountDetails={report.discount_details || []}
        products={report.product_sales || []}
        discounts={report.bill_discounts || 0}
        sales={shift.sales_total}
        upi={shift.upi_sales}
        card={shift.card_sales}
        refunds={shift.refunds}
        expenses={shift.cash_expenses}
        expenseList={(report && report.expense_details) || []}
        counted={shift.counted_cash}
        upiCounted={shift.counted_upi}
      /></>

      <h3 className="sh-h3">Stock differences {mismatched.length > 0 && <b className="sh-count-badge">{mismatched.length}</b>}</h3>
      {mismatched.length === 0 ? (
        <div className="sh-okbox">Every counted item matches the system. </div>
      ) : (
        <StockTable rows={mismatched} highlight />
      )}
      {uncounted.length > 0 && (
        <p className="sh-muted">{uncounted.length} item(s) were not counted and carried forward the system quantity: {uncounted.slice(0, 12).map((r) => r.name).join(', ')}{uncounted.length > 12 ? '…' : ''}</p>
      )}

      <h3 className="sh-h3">
        Stock by category
        <button type="button" className="sh-link" onClick={() => setShowAll((v) => !v)}>{showAll ? 'Show only items that moved' : 'Show all items'}</button>
      </h3>
      <CategoryStock rows={rows} billedTotal={shift.sales_total} />

    </>
  );
}

function StockTable({ rows, highlight }) {
  return (
    <div className="sh-table-wrap">
      <table className="sh-table">
        <thead>
          <tr>
            <th>Item</th>
            <th className="num">Opening</th>
            <th className="num">Received</th>
            <th className="num">Sold</th>
            <th className="num">Adjusted</th>
            <th className="num">Expected</th>
            <th className="num">Counted</th>
            <th className="num">Difference</th>
            <th className="num">Value</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.raw_material_id} className={highlight ? (r.diff < 0 ? 'is-bad' : 'is-warn') : ''}>
              <td><strong>{r.name}</strong><small>{r.category}, {r.unit}</small></td>
              <td className="num">{qty(r.opening)}</td>
              <td className="num">{r.received ? qty(r.received) : '—'}</td>
              <td className="num">{r.sold ? qty(r.sold) : '—'}</td>
              <td className="num">{r.adjusted ? signed(r.adjusted) : '—'}</td>
              <td className="num">{qty(r.expected)}</td>
              <td className="num">{r.counted == null ? 'Not counted' : qty(r.counted)}</td>
              <td className={`num ${r.diff < 0 ? 't-bad' : r.diff > 0 ? 't-warn' : ''}`}>{r.diff == null ? '—' : Math.abs(r.diff) < 1e-9 ? '0' : signed(r.diff)}</td>
              <td className={`num ${r.diff_value < 0 ? 't-bad' : ''}`}>{r.diff_value ? signed(r.diff_value, (v) => rupees(v)) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 && <div className="sh-empty">No items to show.</div>}
    </div>
  );
}

function CashSummary({ opening, cats = [], discountDetails = [], products = [], discounts = 0, sales, upi, card, refunds, expenses, counted, upiCounted, expenseList = [] }) {
  const [openDisc, setOpenDisc] = useState(false);
  const [openCat, setOpenCat] = useState(null);
  const total = (Number(opening) || 0) + (Number(sales) || 0);
  const remaining = total - (Number(upi) || 0) - (Number(card) || 0) - (Number(refunds) || 0) - (Number(expenses) || 0);
  const rawDiff = counted == null ? null : Number(counted) - remaining;
  const diff = rawDiff == null ? null : Math.abs(rawDiff) < 0.005 ? 0 : rawDiff;
  const Row = ({ label, value, cls }) => (
    <div className={`sh-cs-row ${cls || ''}`}><span>{label}</span><b>{value}</b></div>
  );
  return (
    <div className="sh-card sh-cs">
      <h3 className="sh-h3 sh-cs-title">Cash summary</h3>
      <Row label="CB (cash from last shift)" value={rupees(opening, 2)} />
      <div className="sh-cs-group">Sales this shift</div>
      {cats.length === 0 && <Row label="No sales" value={rupees(0, 2)} cls="is-sub" />}
      {cats.map((c) => {
        const isOpen = openCat === c.category;
        const items = products.filter((p) => (p.category || 'Uncategorised') === c.category);
        return (
          <React.Fragment key={c.category}>
            <button type="button" className={`sh-cs-row is-sub sh-cs-cat ${isOpen ? 'is-open' : ''}`}
              onClick={() => setOpenCat(isOpen ? null : c.category)} aria-expanded={isOpen}>
              <span><i>{isOpen ? '▾' : '▸'}</i> {c.category}</span><b>{rupees(c.amount, 2)}</b>
            </button>
            {isOpen && (
              <div className="sh-cs-prods">
                {items.length === 0 && <div className="sh-cs-prod"><span>No item details</span><b /></div>}
                {items.map((p) => (
                  <div key={p.product_id} className="sh-cs-prod">
                    <span>{p.name} <em>× {p.weighted ? `${qty(p.qty)} g` : qty(p.qty)}{p.free_qty ? ` + ${qty(p.free_qty)} free` : ''}</em></span>
                    <b>{rupees(p.amount, 2)}</b>
                  </div>
                ))}
              </div>
            )}
          </React.Fragment>
        );
      })}
      {(discounts > 0.009 || discountDetails.length > 0) && (
        <>
          <button type="button" className={`sh-cs-row is-sub sh-cs-cat ${openDisc ? 'is-open' : ''}`} onClick={() => setOpenDisc(!openDisc)} aria-expanded={openDisc}>
            <span><i>{openDisc ? '▾' : '▸'}</i> Discounts &amp; round-off</span><b>−{rupees(discounts, 2)}</b>
          </button>
          {openDisc && (
            <div className="sh-cs-prods">
              {discountDetails.length === 0 && <div className="sh-cs-prod"><span>No details for this shift</span><b /></div>}
              {discountDetails.map((d) => (
                <div key={d.bill_no} style={{ padding: '4px 0', borderBottom: '1px dashed #ece6f2' }}>
                  <div className="sh-cs-prod"><span><strong>{d.bill_no}</strong> <em>{new Date(`${String(d.bill_date).replace(' ', 'T')}Z`).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })}</em></span><b /></div>
                  {d.lines.map((l, i) => (
                    <div key={i} className="sh-cs-prod">
                      <span>{l.name} <em>× {l.weighted ? `${qty(l.qty)} g` : qty(l.qty)} · MRP {rupees(l.mrp, 2)} → {rupees(l.charged, 2)}</em></span>
                      <b>−{rupees(l.off, 2)}</b>
                    </div>
                  ))}
                  {d.bill_discount > 0 && <div className="sh-cs-prod"><span>Bill discount</span><b>−{rupees(d.bill_discount, 2)}</b></div>}
                  {d.round_off !== 0 && <div className="sh-cs-prod"><span>Round-off</span><b>{d.round_off > 0 ? '+' : '−'}{rupees(Math.abs(d.round_off), 2)}</b></div>}
                </div>
              ))}
              <div className="sh-cs-prod"><span><em>Item discounts are already taken off the category amounts above; the total here is bill discounts and round-off.</em></span><b /></div>
            </div>
          )}
        </>
      )}
      <Row label="Total sales" value={rupees(sales, 2)} cls="is-line" />
      <Row label="Total (CB + sales)" value={rupees(total, 2)} cls="is-strong" />
      <Row label="Less GPay / UPI" value={`−${rupees(upi, 2)}`} />
      {Number(card) > 0 && <Row label="Less card" value={`−${rupees(card, 2)}`} />}
      {Number(refunds) > 0 && <Row label="Less refunds" value={`−${rupees(refunds, 2)}`} />}
      {Number(expenses) > 0 && <Row label="Less cash expenses" value={`−${rupees(expenses, 2)}`} />}
      {expenseList.length > 0 && (
        <div className="sh-cs-prods">
          {expenseList.map((e) => (
            <div className="sh-cs-prod" key={e.id}>
              <span>{e.description || e.category}{e.by && !String(e.description || '').includes('— by') ? <small style={{ color: '#8b84a0' }}> · by {e.by}</small> : null}{e.mode !== 'cash' ? ` (${e.mode.toUpperCase()} – not from drawer)` : ''}</span>
              <span>−{rupees(e.amount, 2)}</span>
            </div>
          ))}
        </div>
      )}
      <Row label="Remaining CB (cash in drawer)" value={rupees(remaining, 2)} cls="is-total" />
      {diff != null && (
        <>
          <Row label="Counted cash" value={rupees(counted, 2)} />
          <Row
            label="Difference"
            value={diff === 0 ? 'Matches' : diff < 0 ? `${rupees(-diff, 2)} short` : `${rupees(diff, 2)} extra`}
            cls={diff < 0 ? 'is-bad' : diff > 0 ? 'is-warn' : 'is-good'}
          />
        </>
      )}
      {upiCounted != null && (() => {
        const raw = Number(upiCounted) - (Number(upi) || 0);
        const ud = Math.abs(raw) < 0.005 ? 0 : raw;
        return (
          <>
            <div className="sh-cs-group">UPI check</div>
            <Row label="UPI from bills" value={rupees(upi, 2)} cls="is-sub" />
            <Row label="UPI in GPay/PhonePe app" value={rupees(upiCounted, 2)} cls="is-sub" />
            <Row label="UPI difference" value={ud === 0 ? 'Matches' : ud < 0 ? `${rupees(-ud, 2)} less in app` : `${rupees(ud, 2)} more in app`}
              cls={ud < 0 ? 'is-bad' : ud > 0 ? 'is-warn' : 'is-good'} />
          </>
        );
      })()}
    </div>
  );
}

function CategoryStock({ rows, billedTotal }) {
  const groups = {};
  rows.forEach((r) => { const c = r.category || 'Uncategorised'; (groups[c] = groups[c] || []).push(r); });
  const cats = Object.keys(groups).sort((a, b) => a.localeCompare(b));
  const valueOf = (r) => (r.sold && r.mrp ? r.sold * r.mrp : 0);
  const subtotals = {};
  cats.forEach((c) => { subtotals[c] = groups[c].reduce((t, r) => t + valueOf(r), 0); });
  const grand = cats.reduce((t, c) => t + subtotals[c], 0);
  const mrpText = (r) => (r.mrp == null ? '—' : r.unit === 'g' ? `${rupees(r.mrp, 2)}/g` : rupees(r.mrp, Number.isInteger(r.mrp) ? 0 : 2));
  return (
    <>
      <div className="sh-table-wrap">
        <table className="sh-table sh-cat-table">
          <thead>
            <tr>
              <th>Item</th>
              <th className="num">MRP</th>
              <th className="num">Opening</th>
              <th className="num">Received</th>
              <th className="num">Sales</th>
              <th className="num">Expected</th>
              <th className="num">Counted</th>
              <th className="num">Sales value</th>
            </tr>
          </thead>
          {cats.map((c) => (
            <tbody key={c}>
              <tr className="sh-cat-row"><td colSpan={8}>{c}</td></tr>
              {groups[c].slice().sort((a, b) => a.name.localeCompare(b.name)).map((r) => (
                <tr key={r.raw_material_id} className={r.diff != null && Math.abs(r.diff) > 1e-9 ? (r.diff < 0 ? 'is-bad' : 'is-warn') : ''}>
                  <td><strong>{r.name}</strong><small>{r.unit}</small></td>
                  <td className="num">{mrpText(r)}</td>
                  <td className="num">{qty(r.opening)}</td>
                  <td className="num">{r.received ? qty(r.received) : '—'}</td>
                  <td className="num">{r.sold ? qty(r.sold) : '—'}</td>
                  <td className="num">{qty(r.expected)}</td>
                  <td className="num">{r.counted == null ? '—' : qty(r.counted)}</td>
                  <td className="num">{valueOf(r) ? rupees(valueOf(r), 2) : '—'}</td>
                </tr>
              ))}
              <tr className="sh-sub-row"><td colSpan={7}>{c} total</td><td className="num">{rupees(subtotals[c], 2)}</td></tr>
            </tbody>
          ))}
          <tfoot>
            <tr className="sh-total-row"><td colSpan={7}>Grand total (sales qty × MRP)</td><td className="num">{rupees(grand, 2)}</td></tr>
          </tfoot>
        </table>
      </div>
      <p className="sh-muted">Actual billed total after discounts and offers: <b>{rupees(billedTotal, 2)}</b>. Rows in red or amber have a count difference.</p>
    </>
  );
}

function ProductSales({ rows, title }) {
  const total = rows.reduce((t, r) => t + r.amount, 0);
  return (
    <>
      <h3 className="sh-h3">{title}</h3>
      {rows.length === 0 ? <div className="sh-empty">No sales yet.</div> : (
        <div className="sh-table-wrap">
          <table className="sh-table">
            <thead><tr><th>Product</th><th className="num">Qty</th><th className="num">Free</th><th className="num">Amount</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.product_id}>
                  <td><strong>{r.name}</strong></td>
                  <td className="num">{r.weighted ? `${qty(r.qty)} g` : qty(r.qty)}</td>
                  <td className="num">{r.free_qty ? qty(r.free_qty) : '—'}</td>
                  <td className="num">{rupees(r.amount, 2)}</td>
                </tr>
              ))}
              <tr className="sh-total-row"><td>Total</td><td /><td /><td className="num">{rupees(total, 2)}</td></tr>
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* ================= History ================= */

function History({ onOpen, onError }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get('/shifts?limit=60').then(setRows).catch((e) => onError(e.message)); }, [onError]);
  if (!rows) return <div className="sh-empty">Loading…</div>;
  if (rows.length === 0) return <div className="sh-empty">No shifts yet.</div>;
  return (
    <div className="sh-table-wrap">
      <table className="sh-table">
        <thead>
          <tr>
            <th>Shift</th><th>Staff</th><th className="num">Bills</th><th className="num">Sales</th>
            <th className="num">Cash diff</th><th className="num">UPI diff</th><th className="num">Stock issues</th><th />
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.id}>
              <td><strong>{s.name}, {niceDate(s.business_date)}</strong><small>{s.status === 'open' ? 'Open now' : `Closed ${localTime(s.closed_at)}`}</small></td>
              <td>{s.opened_by || '—'}{s.closed_by && s.closed_by !== s.opened_by ? ` → ${s.closed_by}` : ''}</td>
              <td className="num">{s.bill_count ?? '—'}</td>
              <td className="num">{s.sales_total != null ? rupees(s.sales_total) : '—'}</td>
              <td className={`num ${s.cash_diff < 0 ? 't-bad' : s.cash_diff > 0 ? 't-warn' : ''}`}>{s.cash_diff == null ? '—' : s.cash_diff === 0 ? '0' : signed(s.cash_diff, (v) => rupees(v))}</td>
              <td className={`num ${s.upi_diff < 0 ? 't-bad' : s.upi_diff > 0 ? 't-warn' : ''}`}>{s.upi_diff == null ? '—' : Math.abs(s.upi_diff) < 0.005 ? '0' : signed(s.upi_diff, (v) => rupees(v))}</td>
              <td className={`num ${s.mismatches ? 't-bad' : ''}`}>{s.mismatches == null ? '—' : s.mismatches === 0 ? 'None' : `${s.mismatches} items, ${rupees(s.shortage_value)} short`}</td>
              <td className="num">{s.status === 'closed' && <button type="button" className="sh-link" onClick={() => onOpen(s.id)}>View</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
